import { ShieldCheck } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  useBulkApproveCaseContextMutation,
  useBulkUnapproveCaseContextMutation,
} from '@/services/api/caseContextApprovalService';
import { CaseContext } from '@/services/types/case-context-types';

const APPROVAL_FIELDS = [
  'coverage_details',
  'potential_max_coverage',
  'liability_description',
  'icd_10_codes',
  'extent_of_care',
  'medical_chronology',
  'total_medical_bills',
  'unused_docs',
  'client_narrative',
];

interface Props {
  matterId: string;
  matterContext?: CaseContext | null;
}

export const CaseContextBulkApproval: React.FC<Props> = ({
  matterId,
  matterContext,
}) => {
  const [bulkApprove, { isLoading: isApproving }] =
    useBulkApproveCaseContextMutation();
  const [bulkUnapprove, { isLoading: isUnapproving }] =
    useBulkUnapproveCaseContextMutation();
  const [optimisticState, setOptimisticState] = useState<boolean | null>(null);

  // Calculate if all fields are approved
  const allFieldsApproved = useMemo(() => {
    if (!matterContext) return false;
    return APPROVAL_FIELDS.every((field) => {
      const approvalKey = `${field}_approval` as keyof CaseContext;
      const approval = matterContext[approvalKey];
      return (
        approval &&
        typeof approval === 'object' &&
        'approved' in approval &&
        approval.approved === true
      );
    });
  }, [matterContext]);

  const isAllApproved =
    optimisticState !== null ? optimisticState : allFieldsApproved;
  const isLoading = isApproving || isUnapproving;

  const handleToggle = async (checked: boolean) => {
    setOptimisticState(checked);

    try {
      if (checked) {
        await bulkApprove({ matterId }).unwrap();
        toast.success('All case context fields approved');
      } else {
        await bulkUnapprove({ matterId }).unwrap();
        toast.success('All approvals revoked');
      }
      setOptimisticState(null);
    } catch (error) {
      setOptimisticState(null);
      toast.error(
        checked
          ? 'Failed to approve all fields'
          : 'Failed to revoke all approvals',
      );
    }
  };

  return (
    <div className="flex items-center space-x-2">
      <Switch
        id="bulk-approval"
        checked={isAllApproved}
        onCheckedChange={handleToggle}
        disabled={isLoading}
        className={cn(
          'data-[state=checked]:bg-green-600 h-6 w-11',
          isLoading && 'opacity-50 cursor-wait',
          // Override the thumb translation
          '[&>span]:data-[state=checked]:translate-x-5',
        )}
      />
      <Label
        htmlFor="bulk-approval"
        className="text-sm font-medium cursor-pointer flex items-center gap-1"
      >
        <ShieldCheck className="h-4 w-4" />
        {isAllApproved ? 'All Approved' : 'Approve All'}
      </Label>
    </div>
  );
};
